/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

// Suppress ResizeObserver loop errors globally
const resizeObserverErrorHandler = (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded') {
    e.stopImmediatePropagation();
    return false;
  }
};

// Add error handler for ResizeObserver errors
window.addEventListener('error', resizeObserverErrorHandler);

// Also handle for unhandled promise rejections in case ResizeObserver errors are async
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && typeof e.reason.message === 'string' && 
      (e.reason.message.includes('ResizeObserver loop') || 
       e.reason.message.includes('ResizeObserver loop completed with undelivered notifications'))) {
    e.preventDefault();
    return false;
  }
});

// Override console.error to filter out ResizeObserver errors
const originalConsoleError = console.error;
console.error = function(...args) {
  // Quick check for ResizeObserver errors using first string argument
  const firstStringArg = args.find(arg => typeof arg === 'string');
  if (firstStringArg && 
      (firstStringArg.includes('ResizeObserver loop') || 
       firstStringArg.includes('ResizeObserver loop completed with undelivered notifications'))) {
    return; // Silently ignore ResizeObserver console errors
  }
  
  // For other errors, just pass them through to avoid conversion issues
  originalConsoleError.apply(console, args);
};

import { createApp } from 'vue'
import TaskPane from '../components/TaskPane.vue'
import store from '../store'
import '../assets/main.css'
import IconProvider from '../components/commons/IconProvider.vue'
import OutlookWidthManager from '../utils/outlookWidthManager.js'
import DatadogService from '../service/DatadogService.js'
import { restoreNotificationBannersForEmail } from '../service/Actions.js'
import { createVuetify } from 'vuetify'
import { aliases, mdi } from 'vuetify/iconsets/mdi'
import 'vuetify/styles'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
    }
  },
  ssr: false // Disable SSR for Office Add-in
})

// Function to handle window resizing and prevent content overflow
function setupResizeHandler() {
  const handleResize = () => {
    const app = document.getElementById('app');
    const body = document.body;
    const html = document.documentElement;
    
    if (app && body && html) {
      // Ensure minimum width is maintained
      const minWidth = window.innerWidth < 400 ? 350 : 400;
      
      // Set styles to prevent overflow
      [app, body, html].forEach(element => {
        element.style.minWidth = `${minWidth}px`;
        element.style.maxWidth = '100%';
        element.style.overflowX = 'hidden';
        element.style.boxSizing = 'border-box';
        element.style.width = '100%';
      });
      
      // Handle specific content containers
      const containers = document.querySelectorAll('.content-section, .section-card, .selected-email-container');
      containers.forEach(container => {
        container.style.maxWidth = '100%';
        container.style.overflowX = 'hidden';
        container.style.wordWrap = 'break-word';
        container.style.boxSizing = 'border-box';
      });
      
      // Handle text elements that might overflow
      const textElements = document.querySelectorAll('.email-subject, .email-from, .email-received');
      textElements.forEach(element => {
        element.style.wordWrap = 'break-word';
        element.style.overflowWrap = 'break-word';
        element.style.hyphens = 'auto';
        element.style.msHyphens = 'auto'; // IE support
      });
    }
  };
  
  // Initial setup
  handleResize();
  
  // Listen for resize events
  window.addEventListener('resize', handleResize);
  
  // Also handle when content changes
  const observer = new MutationObserver(handleResize);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class']
  });
  
  return { handleResize, observer };
}

// Email change detection system
let currentEmailId = null;
let emailChangeInterval = null;

// Function to detect email changes and update store
function setupEmailChangeDetection() {
  console.log('Setting up email change detection...');
  
  // Function to check if email has changed
  const checkEmailChange = async () => {
    try {
      if (typeof Office !== "undefined" && Office.context?.mailbox?.item) {
        const item = Office.context.mailbox.item;
        const newEmailId = item.internetMessageId || item.itemId;
        
        // Check if email has changed
        if (newEmailId && currentEmailId && newEmailId !== currentEmailId) {
          console.log('Email change detected!', {
            old: currentEmailId,
            new: newEmailId
          });
          
          // Update current email ID
          currentEmailId = newEmailId;
          
          // Clear previous state
          store.commit('email/SET_LOADING', true);
          // Clear assignments and actions when switching emails
          store.commit('assignments/CLEAR_SELECTED_ASSIGNMENT');
          // store.commit('actions/RESET_ACTIONS_STATE');
          
          // Load new email data
          await store.dispatch('email/loadCurrentEmail');
          
          // Restore notification banners for this email after a small delay
          setTimeout(() => {
            restoreNotificationBannersForEmail();
          }, 500);
          
        } else if (newEmailId && !currentEmailId) {
          // First time loading
          console.log('Initial email loaded:', newEmailId);
          currentEmailId = newEmailId;
          await store.dispatch('email/loadCurrentEmail');
          
          // Restore notification banners for initial email after a small delay
          setTimeout(() => {
            restoreNotificationBannersForEmail();
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error checking email change:', error);
    }
  };
  
  // Check immediately
  checkEmailChange();
  
  // Set up polling every 1 second
  emailChangeInterval = setInterval(checkEmailChange, 1000);
  
  return emailChangeInterval;
}

// Function to stop email change detection
function stopEmailChangeDetection() {
  if (emailChangeInterval) {
    console.log('Stopping email change detection');
    clearInterval(emailChangeInterval);
    emailChangeInterval = null;
  }
}

// Function to handle Office ItemChanged event (backup method)
function setupItemChangeListener() {
  console.log('Setting up Office ItemChanged event listener...');
  
  if (Office.context?.mailbox?.addHandlerAsync) {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      async (eventArgs) => {
        console.log('Office ItemChanged event triggered');
        try {
          // Small delay to ensure new item is loaded
          setTimeout(async () => {
            await checkAndUpdateEmail();
          }, 200);
        } catch (error) {
          console.error('Error handling ItemChanged event:', error);
        }
      },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log('ItemChanged event handler registered successfully');
        } else {
          console.warn('Failed to register ItemChanged event handler:', result.error);
        }
      }
    );
  } else {
    console.warn('Office ItemChanged events not supported');
  }
}

// Helper function to check and update email
async function checkAndUpdateEmail() {
  try {
    if (Office.context?.mailbox?.item) {
      const item = Office.context.mailbox.item;
      const newEmailId = item.internetMessageId || item.itemId;
      
      if (newEmailId !== currentEmailId) {
        console.log('Updating email after change detection');
        currentEmailId = newEmailId;
        
        // Clear state and load new email
        store.commit('email/SET_LOADING', true);
        // Clear assignments and actions when switching emails
        store.commit('assignments/CLEAR_SELECTED_ASSIGNMENT');
        // store.commit('actions/RESET_ACTIONS_STATE');
        
        await store.dispatch('email/loadCurrentEmail');
        
        // Restore notification banners for this email after a small delay
        setTimeout(() => {
          restoreNotificationBannersForEmail();
        }, 500);
        
      }
    }
  } catch (error) {
    console.error('Error updating email:', error);
    store.commit('email/SET_LOADING', false);
  }
}

// Datadog initialization function
function initializeDatadog() {
  try {
    // Get Office context for additional metadata
    const officeContext = {
      host: typeof Office !== 'undefined' && Office.context ? Office.context.host : 'unknown',
      platform: typeof Office !== 'undefined' && Office.context ? Office.context.diagnostics?.platform : 'unknown'
    };

    // Initialize Datadog with configuration
    DatadogService.init({
      userEmail: null, // Will be set later when user authentication completes
      environment: process.env.VITE_APP_ENV || 'development',
      officeContext
    });

    // Track application startup
    DatadogService.addAction('app_startup', {
      office_ready: typeof Office !== 'undefined',
      environment: process.env.VITE_APP_ENV || 'development'
    });

  } catch (error) {
    console.error('Failed to initialize Datadog:', error);
    // Don't let Datadog initialization errors break the app
  }
}

// Ensure Office.js is loaded before initializing
function initializeApp() {
  console.log('🚀 Initializing TOPS2 Actions Add-in...');
  
  try {
    // Initialize Datadog first (before any potential errors)
    initializeDatadog();
    console.log('✅ Datadog initialization attempted');
    
    // Initialize width management first
    OutlookWidthManager.init();
    console.log('✅ Width manager initialized');
    
    // Setup resize handler
    setupResizeHandler();
    console.log('✅ Resize handler setup');
    
    // Create and mount the Vue app after Office.js is ready
    const app = createApp(TaskPane)
    app.component('IconProvider', IconProvider)

    app.config.compilerOptions.isCustomElement = tag => {
      return tag.startsWith('IconProvider')
    }
    
    // Add global error handler with Datadog integration
    app.config.errorHandler = (err, vm, info) => {
      // Suppress ResizeObserver errors
      if (err && err.message && 
          (err.message.includes('ResizeObserver loop') || 
           err.message.includes('ResizeObserver loop completed with undelivered notifications'))) {
        return; // Silently ignore ResizeObserver errors
      }
      
      console.error('Vue Error:', err);
      console.error('Component:', vm?.$options?.name || vm?.type?.name || 'Unknown Component');
      console.error('Info:', typeof info === 'string' ? info : JSON.stringify(info, null, 2));
      
      // Track error in Datadog
      DatadogService.addError(err, {
        component: vm?.$options?.name || 'unknown',
        info,
        error_type: 'vue_error'
      });
    }
    
    app.use(store)
    app.use(vuetify)
    console.log('✅ Vue app created with store and vuetify');

    // Initialize the store's Office state
    store.commit('SET_OFFICE_READY', true)
    console.log('✅ Office state set to ready');
    
    // Mount the app
    const mountResult = app.mount('#app')
    console.log('✅ Vue app mounted:', mountResult);
    
    // Hide loading fallback once Vue is mounted
    const loadingFallback = document.getElementById('loading-fallback');
    if (loadingFallback) {
      loadingFallback.style.display = 'none';
      console.log('✅ Loading fallback hidden');
    }
    
    // Apply width settings after mount
    setTimeout(() => {
      OutlookWidthManager.init();
      console.log('🔧 Width settings applied:', OutlookWidthManager.getCurrentWidth());
    }, 200);
    
    // Setup email change detection after app is mounted
    setTimeout(() => {
      setupEmailChangeDetection();
      setupItemChangeListener(); // Backup method
      console.log('✅ Email change detection setup');
    }, 500);
    
    // Make cleanup functions globally available
    window.stopEmailChangeDetection = stopEmailChangeDetection;
    window.OutlookWidthManager = OutlookWidthManager;
    
    // Setup resize handler again after mount to ensure everything is properly configured
    setTimeout(() => {
      setupResizeHandler();
      console.log('✅ Final resize handler setup complete');
    }, 100);
    
    console.log('🎉 TOPS2 Actions Add-in initialized successfully');
    
  } catch (error) {
    console.error('❌ Error initializing app:', error);
    
    // Fallback: try to show a basic error message
    const appDiv = document.getElementById('app');
    if (appDiv) {
      appDiv.innerHTML = `
        <div style="padding: 20px; color: red; font-family: Arial, sans-serif;">
          <h2>Error Loading Add-in</h2>
          <p>There was an error loading the TOPS2 Actions add-in.</p>
          <p><strong>Error:</strong> ${error.message}</p>
          <p>Please refresh the task pane or contact support.</p>
          <button onclick="window.location.reload()" style="padding: 8px 16px; margin-top: 10px;">
            Refresh
          </button>
        </div>
      `;
    }
  }
}

// Initialize Office.js before mounting Vue
if (typeof Office !== 'undefined') {
  Office.onReady(async (info) => {
    console.log('Office.js is ready, host:', info.host);
    if (info.host === Office.HostType.Outlook || true) {
      initializeApp();
    }
  });
} else {
  // Fallback if Office.js is not loaded yet
  console.warn('Office.js not found, waiting for it to load...');
  const checkOffice = setInterval(() => {
    if (typeof Office !== 'undefined') {
      clearInterval(checkOffice);
      Office.onReady(async (info) => {
        console.log('Office.js is ready (delayed), host:', info.host);
        if (info.host === Office.HostType.Outlook || true) {
          initializeApp();
        }
      });
    }
  }, 100);
  
  // Timeout fallback - initialize after 5 seconds even if Office.js is not ready
  setTimeout(() => {
    if (typeof Office === 'undefined') {
      console.error('Office.js failed to load, initializing without it');
      clearInterval(checkOffice);
      // Initialize without Office.js for development purposes
      if (process.env.NODE_ENV === 'development') {
        initializeApp();
      }
    }
  }, 5000);
}